import { useCurrentShop } from '#storefront/composables'

export function useFormat() {
  const currentShop = useCurrentShop()

  const formatterKm = new Intl.NumberFormat(
    currentShop.value.locale ?? 'en-GB',
    {
      maximumFractionDigits: 1,
      style: 'unit',
      unit: 'kilometer',
    },
  )

  const formatterMeter = new Intl.NumberFormat(
    currentShop.value.locale ?? 'en-GB',
    {
      maximumSignificantDigits: 3,
      style: 'unit',
      unit: 'meter',
    },
  )

  const formatterDate = new Intl.DateTimeFormat(currentShop.value.locale, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  })

  /**
   * Format a Date object in a numeric format according to the current shop's locale
   * e.g. 25.12.2024 (DE), 25/12/2024 (UK), 12/25/2024 (US)
   * @param date The Date object to be formatted
   * @returns
   */
  const formatDate = (date: Date): string | undefined => {
    if (!date || isNaN(date.valueOf())) {
      return undefined
    }

    return formatterDate.format(date)
  }

  /**
   * Format a distance according to the current shop's locale
   * Uses meters for short distances (<1000) and kilometers for longer distances
   *
   * @param distance The distance to be formatted in meters
   * @returns
   */
  const formatDistance = (distance: number): string | undefined => {
    if (isNaN(distance)) {
      return undefined
    }

    return distance < 1000
      ? formatterMeter.format(distance)
      : formatterKm.format(distance / 1000)
  }

  /**
   * Format shoe sizes by converting decimals to proper fractions
   * e.g. 41.333 → 41⅓, 42.666 → 42⅔, 42.5 → 42½, 42 → 42
   * Also handles ranges: 35-37 → 35-37, 35/37 → 35/37, 7.5-8 → 7½-8
   * @param sizeLabel The size label string to be formatted
   * @returns Formatted size string
   */
  const formatShoeSize = (sizeLabel: string | undefined): string => {
    if (!sizeLabel) {
      return ''
    }

    const fractions: Record<number, string> = {
      0.125: '⅛',
      0.25: '¼',
      0.333: '⅓',
      0.375: '⅜',
      0.5: '½',
      0.625: '⅝',
      0.666: '⅔',
      0.75: '¾',
      0.875: '⅞',
    }

    // Helper functions to format a single size value
    const formatSingleSize = (size: string): string => {
      const numericSize = parseFloat(size)
      if (isNaN(numericSize)) {
        return size
      }

      const integerPart = Math.floor(numericSize)
      const fractionalPart = numericSize - integerPart

      for (const [decimal, symbol] of Object.entries(fractions)) {
        if (Math.abs(fractionalPart - Number(decimal)) < 0.01) {
          return `${integerPart}${symbol}`
        }
      }

      if (fractionalPart < 0.01) {
        return integerPart.toString()
      }

      return numericSize.toFixed(1).replace(/\.0$/, '')
    }

    // Check if it's a range with dash (e.g., "35-37", "7.5-8")
    const dashRangeMatch = sizeLabel.match(/^([\d.]+)[\s-]+([\d.]+)$/)
    if (dashRangeMatch) {
      const start = formatSingleSize(dashRangeMatch[1])
      const end = formatSingleSize(dashRangeMatch[2])
      return `${start}-${end}`
    }

    // Check if it's a range with slash (e.g., "35/37", "7.5/8")
    const slashRangeMatch = sizeLabel.match(/^([\d.]+)\/([\d.]+)$/)
    if (slashRangeMatch) {
      const start = formatSingleSize(slashRangeMatch[1])
      const end = formatSingleSize(slashRangeMatch[2])
      return `${start}/${end}`
    }

    // Single size
    return formatSingleSize(sizeLabel)
  }

  return {
    formatDate,
    formatDistance,
    formatShoeSize,
  }
}
